import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { EmployeeProfile } from '@/types'
import { CheckCircle, Plus, Star } from 'lucide-react'

interface EmployeeHeroHeaderProps {
  employee: EmployeeProfile
  isScrolled: boolean
  onAddToWorkspace: (employee: EmployeeProfile) => void
}

/**
 * Employee Hero and Header Component
 * 
 * Contains both the sticky header and hero section for employee profile pages:
 * - Sticky header that appears on scroll with glass background
 * - Hero section with gradient background, employee info, and CTA
 */
export function EmployeeHeroHeader({ employee, isScrolled, onAddToWorkspace }: EmployeeHeroHeaderProps) {
  return (
    <>
      {/* Sticky Header */}
      <div className={cn(
        "fixed top-16 left-0 right-0 z-40 transition-all duration-300",
        isScrolled
          ? "translate-y-0 opacity-100"
          : "-translate-y-full opacity-0"
      )}>
        <div className="container mx-auto px-6 py-3">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 backdrop-blur-md bg-background/80 border border-border/50 shadow-md rounded-lg">
              <div className="px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border shadow-sm flex-shrink-0">
                      <img
                        src={employee.avatar}
                        alt={employee.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h2 className="text-base font-medium text-foreground">
                        {employee.name}
                      </h2>
                    </div>
                  </div>
                  <Button
                    onClick={() => onAddToWorkspace(employee)}
                    size="sm"
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add to Workspace</span>
                  </Button>
                </div>
              </div>
            </div>
            <div className="lg:col-span-4 hidden lg:block">
              {/* Empty space to maintain grid layout */}
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section with Gradient Background */}
      <div id="hero-section" className="bg-gradient-to-b from-blue-50 to-main-content dark:from-blue-950/30 dark:to-main-content">
        <div className="container mx-auto px-6 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Product Icon and Basic Info */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-white border shadow-sm">
                <img
                  src={employee.avatar}
                  alt={employee.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Product Details */}
            <div className="flex-1 space-y-3">
              <div>
                <h1 className="text-2xl font-semibold text-foreground mb-1">
                  {employee.name}
                </h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <span>Developed by {employee.developer.name}</span>
                  {employee.developer.verified && (
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                  )}
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }, (_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          "w-4 h-4",
                          i < Math.floor(employee.averageRating)
                            ? "text-yellow-400 fill-yellow-400"
                            : "text-gray-300"
                        )}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium">{employee.averageRating}</span>
                  <span className="text-sm text-muted-foreground">
                    ({employee.totalReviews} ratings)
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {employee.description}
                </p>
              </div>

              {/* Action Buttons */}
              <Button
                onClick={() => onAddToWorkspace(employee)}
                size="default"
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add to Workspace</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

EmployeeHeroHeader.displayName = "EmployeeHeroHeader"